import type { MenuProduct } from "@/types";
import { formatSAR } from "@/utils/format";

interface ProductCardProps {
  product: MenuProduct;
  categoryName: string;
  onToggle: (id: string, value: boolean) => void;
  onEdit: (product: MenuProduct) => void;
  onDelete: (product: MenuProduct) => void;
}

export default function ProductCard({
  product,
  categoryName,
  onToggle,
  onEdit,
  onDelete,
}: ProductCardProps) {
  const outOfStock = product.stock <= 0;
  const lowStock = product.stock > 0 && product.stock <= 5;

  return (
    <article className="yc-fade-up yc-card-hover bg-white rounded-card border border-background-200 shadow-card overflow-hidden flex flex-col">
      <div className="relative w-full h-40 bg-background-100">
        <img
          src={product.image}
          alt={product.name}
          title={`${product.name} — المنيو`}
          className="w-full h-full object-cover object-top"
        />
        <span className="absolute top-3 start-3 rounded-full bg-white/95 px-2.5 py-1 text-xs font-semibold text-foreground-700">
          {categoryName}
        </span>
        {outOfStock && (
          <span className="absolute top-3 end-3 rounded-full bg-red-500 px-2.5 py-1 text-xs font-semibold text-white">
            نفذت الكمية
          </span>
        )}
      </div>

      <div className="flex-1 p-4 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-base font-bold text-foreground-950">
            {product.name}
          </h3>
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${
              lowStock
                ? "bg-accent-100 text-accent-900"
                : "bg-background-100 text-foreground-600"
            }`}
          >
            متوفر {product.stock}
          </span>
        </div>
        <p className="mt-1.5 text-sm leading-6 text-foreground-500 line-clamp-2">
          {product.description}
        </p>

        <div className="mt-4 flex items-center justify-between">
          <span className="text-lg font-bold text-primary-700">
            {formatSAR(product.price)}
          </span>

          <button
            type="button"
            role="switch"
            aria-checked={product.isAvailable}
            onClick={() => onToggle(product.id, !product.isAvailable)}
            className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1.5 text-xs font-semibold transition-colors duration-200 cursor-pointer whitespace-nowrap ${
              product.isAvailable
                ? "bg-primary-100 text-primary-800"
                : "bg-background-100 text-foreground-500"
            }`}
          >
            <span
              className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${
                product.isAvailable ? "bg-primary-500" : "bg-background-300"
              }`}
            >
              <span
                className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all duration-200 ${
                  product.isAvailable ? "start-4" : "start-0.5"
                }`}
              ></span>
            </span>
            {product.isAvailable ? "متاح" : "موقوف"}
          </button>
        </div>

        <div className="mt-4 pt-3 border-t border-background-100 flex items-center gap-2">
          <button
            type="button"
            onClick={() => onEdit(product)}
            className="flex-1 h-10 rounded-btn bg-background-100 text-foreground-700 text-sm font-semibold hover:bg-primary-100 hover:text-primary-800 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-edit-line"></i>
              تعديل
            </span>
          </button>
          <button
            type="button"
            onClick={() => onDelete(product)}
            className="w-10 h-10 flex items-center justify-center rounded-btn bg-background-100 text-foreground-500 hover:bg-red-100 hover:text-red-600 transition-colors duration-200 cursor-pointer"
            aria-label="حذف المنتج"
          >
            <i className="ri-delete-bin-6-line"></i>
          </button>
        </div>
      </div>
    </article>
  );
}